import Button from 'tio-ui/components/button';
import { dropTask } from 'ember-concurrency';
import Input from 'tio-ui/components/input';
import Switch from 'tio-ui/components/switch';
import {
  PhClock,
  PhDeviceMobile,
  PhLock,
  PhPaperPlaneTilt,
  PhSealCheck,
} from 'ember-phosphor-icons';
import { Section } from 'tio-ui/components/layout';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import formatPhone from 'tio-common/helpers/format-phone';
import Heading from 'tio-ui/components/heading';
import inputmask from 'tio-common/modifiers/inputmask';
import PhoneInput from 'tio-ui/components/phone-input';
import MfaVerification from './mfa-verification';
import Modal from 'tio-ui/components/modal';
import PhoneVerificationFlow from '../../phone-verification/flow';
import safeFormatDate from 'tio-common/helpers/safe-format-date';
import StatusBadge from 'tio-ui/components/status-badge';
import { not } from 'tio-ui/utilities';
import {
  reloadRecord,
  saveExistingRecordWithChangelog,
  saveNewRecordWithChangelog,
  saveRecord,
} from 'tio-data/requests/save-record';
import type { EmployeeExtension as EmployeeModel } from 'tio-data/data/resources/employee.ext';
import type FeaturesService from 'tio-common/services/features';
import type { LoginExtension as LoginModel } from 'tio-data/data/resources/login.ext';
import type { PersonExtension as PersonModel } from 'tio-data/data/resources/person.ext';
import type Owner from '@ember/owner';
import type { PhoneNumber } from 'tio-data/data/resources/phone-number.type';
import type SessionContextService from '../../../../services/session-context';
import type SessionService from '../../../../services/session';
import type { Store } from '@warp-drive/core';

export interface SecuritySettingsSignature {
  Args: {
    employee?: EmployeeModel;
    familyPerson?: PersonModel;
  };
}

/** Any already-dialable international value, whatever its region. */
function isE164(value: string): boolean {
  return /^\+[1-9]\d{9,14}$/.test(value.trim());
}

/**
 * Best-effort E.164 for a value typed or stored without one. US-only by construction —
 * PhoneInput resolves the region properly wherever it is in use, and this is the
 * fallback for the paths that still have no selector.
 */
function toUsE164(value: string): string {
  const digits = value.replace(/\D/g, '');
  if (digits.length === 10) {
    return `+1${digits}`;
  }
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+${digits}`;
  }
  return value;
}

export default class SecuritySettings extends Component<SecuritySettingsSignature> {
  @service declare features: FeaturesService;
  @service declare session: SessionService;
  @service declare sessionContext: SessionContextService;
  @service declare store: Store;

  @tracked showPhoneVerification = false;

  @tracked isOpen = true;
  @tracked isLoading = false;
  @tracked error = null;
  @tracked isEditingContact = false;
  @tracked phoneValue!: string;
  @tracked verificationMode?: 'mfa-enable' | 'phone-change' | 'mfa-phone-entry';
  @tracked showPhoneChangeWarning = false;
  @tracked showMfaPhoneEntry = false;
  @tracked mfaPhoneEntryValue = '';
  @tracked phoneE164: string | null = null;
  /**
   * The record keeps its old `isVerified` until the server answers, and nothing may
   * read that as proof of a number the member has not verified.
   */
  @tracked private isPhoneChangeInFlight = false;
  @tracked mfaPhoneEntryE164: string | null = null;
  @tracked codeSentAt?: number;
  @tracked codeSentForMode?: 'mfa-enable' | 'mfa-phone-entry';
  @tracked codeSentForPhone?: string;

  constructor(owner: Owner, args: SecuritySettingsSignature['Args']) {
    super(owner, args);
    this.setInitialPhoneValue();
  }

  get person() {
    return this.args.familyPerson ?? this.args.employee?.person;
  }

  get primaryPhoneNumber() {
    return this.args.employee?.primaryPhoneNumber ?? this.person?.primaryPhoneNumber;
  }

  /**
   * The RFC 110 states replace the plain phone row only for an employee: a family
   * member's row has no mobile-only helper behind it, so it keeps today's markup.
   */
  get showCaptureStates() {
    return !!this.args.employee && this.features.can('phoneCapture.surfaces.profile');
  }

  get captureMobile() {
    return this.args.employee?.activeMobilePhoneNumber;
  }

  get captureState() {
    return this.args.employee?.phoneVerificationState ?? 'none';
  }

  get isCaptureVerified() {
    return this.captureState === 'verified';
  }

  get isCaptureUnverified() {
    return this.captureState === 'unverified';
  }

  /** Empty rather than undefined, so the display helper and the flow's prefill both
   * take it directly; empty is falsy in the template, which is the branch we want. */
  get captureNumber(): string {
    return this.captureMobile?.number ?? '';
  }

  get captureVerifiedAt() {
    return this.captureMobile?.verifiedAt;
  }

  get isSmsConsentOn() {
    return this.captureMobile?.smsConsent ?? false;
  }

  /**
   * The toggle stays disabled in both states, but for different reasons: unverified
   * numbers are not allowed text updates at all, and the opt-in endpoint is not
   * deployed yet.
   */
  get smsConsentHint() {
    return this.isCaptureVerified
      ? 'profile.phone_capture.text_updates_hint_pending'
      : 'profile.phone_capture.text_updates_hint_unverified';
  }

  /**
   * Prefills the flow and opens it at the number step. Entering at the code step
   * would claim "we sent a code" for a challenge nothing has started — only the
   * flow's own send button starts one.
   */
  openPhoneVerification = () => {
    this.showPhoneVerification = true;
  };

  /**
   * Refetches on any exit, not just success: `submit` persists the number before the
   * code step, so abandoning it still changed what the row should show.
   */
  closePhoneVerification = () => {
    this.showPhoneVerification = false;
    this.refreshPhoneNumbers.perform();
  };

  onPhoneVerified = () => {
    this.showPhoneVerification = false;
    this.refreshPhoneNumbers.perform();
  };

  refreshPhoneNumbers = dropTask(async () => {
    if (!this.args.employee) {
      return;
    }

    await reloadRecord(this.store, this.args.employee, { include: 'phone-numbers' });
    this.setInitialPhoneValue();
  });

  setInitialPhoneValue() {
    this.phoneValue = toUsE164(this.primaryPhoneNumber?.number || '');
  }

  get login() {
    return this.person?.user?.logins?.find((login) => login.isActive);
  }

  get userName() {
    return this.login ? this.login.userName : '';
  }

  get isMfaEnabled() {
    return this.login?.twoFactorEnabled ?? false;
  }

  get mfaPhoneNumber() {
    return this.primaryPhoneNumber?.number || this.phoneValue || '';
  }

  get isMfaToggleDisabled() {
    return this.updateMfaSettings.isRunning || this.isPhoneChangeInFlight;
  }

  get showMfaPhonePrompt() {
    return !this.isPhoneNumberValid && !this.isMfaEnabled;
  }

  get showMfaPhoneNumber() {
    return !this.showMfaPhonePrompt && !!this.mfaPhoneNumber;
  }

  toggleExpand = () => {
    this.isOpen = !this.isOpen;
  };

  toggleContactEdit = () => {
    this.isEditingContact = !this.isEditingContact;
    this.phoneE164 = null;
    this.setInitialPhoneValue();
  };

  setPhoneValue = (value: string) => {
    // Store raw value during editing; normalization happens on blur
    this.phoneValue = value || '';
    // A keystroke outdates the resolved number: PhoneInput re-reports it on blur, and
    // the masked field never reports one at all.
    this.phoneE164 = null;
  };

  // PhoneInput reports E.164 on blur, or null when the number cannot be dialled.
  setValidPhoneValue = (e164: string | null) => {
    this.phoneE164 = e164;
  };

  onPhoneBlur = () => {
    this.normalizePhoneValue();
  };

  private normalizePhoneValue() {
    this.phoneValue = toUsE164(this.phoneValue);
  }

  get isPhoneNumberValid() {
    // Only the shared field yields a number outside the NANP, so an E.164 shape counts
    // only where it is driving — the masked field's verdict must not move.
    if (this.showCaptureStates && (this.phoneE164 || isE164(this.phoneValue))) {
      return true;
    }

    const digits = this.phoneValue.replace(/\D/g, '');
    return digits.length === 10 || (digits.length === 11 && digits.startsWith('1'));
  }

  get isSubmitDisabled() {
    // While the shared field is driving, its verdict is the only one that counts: the
    // digit-count fallback would accept a NANP shape PhoneInput itself rejects.
    if (this.showCaptureStates && this.isEditingContact && !this.phoneE164) {
      return true;
    }

    if (!this.isPhoneNumberValid) {
      return true;
    }

    return !this.isPhoneNumberEdited;
  }

  /**
   * Canonical comparison stops an unchanged legacy national-format number reading as an
   * edit, but that changes what Save and the MFA warning do, so it waits for the flag.
   */
  get isPhoneNumberEdited() {
    return this.showCaptureStates
      ? this.normalizedPhoneValue !== this.storedNumberCanonical
      : this.phoneValue !== this.primaryPhoneNumber?.number;
  }

  get normalizedPhoneValue() {
    // PhoneInput resolves the region properly; the fallback below assumes +1, which is
    // wrong for the MX and CA numbers the selector offers.
    return this.phoneE164 ?? toUsE164(this.phoneValue);
  }

  /**
   * The stored number in the same shape as the field's, so an unchanged legacy
   * national-format record does not read as an edit.
   */
  get storedNumberCanonical() {
    return toUsE164(this.primaryPhoneNumber?.number ?? '');
  }

  savePhoneNumber = dropTask(async () => {
    this.normalizePhoneValue();

    if (this.isMfaEnabled && this.primaryPhoneNumber && this.isPhoneNumberEdited) {
      this.showPhoneChangeWarning = true;
      return;
    }

    await this.doSavePhone();
  });

  private async doSavePhone() {
    this.normalizePhoneValue();
    this.clearCodeSentState();
    const normalizedPhone = this.normalizedPhoneValue;

    this.isPhoneChangeInFlight = true;
    try {
      if (this.primaryPhoneNumber) {
        this.primaryPhoneNumber.number = normalizedPhone;
        // WarpDrive save helper (Phase 2.7) — replaces
        // record.save({ adapterOptions: { changelog } }) on an EXISTING record.
        await saveExistingRecordWithChangelog(
          this.store,
          this.primaryPhoneNumber,
          'updated by employee'
        );
      } else if (this.person?.user) {
        const newPhoneNumber = this.store.createRecord<PhoneNumber>('phone-number', {
          contactType: 'TELEPHONE',
          number: normalizedPhone,
          phoneNumberType: 'MOBILE',
          user: this.person.user,
        });
        // WarpDrive save helper (Phase 2.7) — replaces
        // record.save({ adapterOptions: { changelog } }) on a NEW record. The
        // `_changelog` is a write-only PaperTrail attribute (not a model
        // attribute), so it is injected into the serialized POST body by
        // saveNewRecordWithChangelog rather than dropped by plain saveNewRecord.
        await saveNewRecordWithChangelog(this.store, newPhoneNumber, 'added by employee');
      }

      if (this.args.employee) {
        // Standalone refresh (NOT after a builder mutation on this record) to pick
        // up the just-saved phone relationship — keep the refetch via the helper.
        await reloadRecord(this.store, this.args.employee, { include: 'phone-numbers' });
      } else if (this.args.familyPerson) {
        await reloadRecord(this.store, this.args.familyPerson, { include: 'phone-numbers' });
      }
    } finally {
      // A failed save leaves the record holding the new number, so the lock has to
      // lift on the error path too or MFA stays unreachable.
      this.isPhoneChangeInFlight = false;
    }

    this.toggleContactEdit();
  }

  confirmPhoneChange = () => {
    this.showPhoneChangeWarning = false;
    this.verificationMode = 'phone-change';
  };

  cancelPhoneChange = () => {
    this.showPhoneChangeWarning = false;
  };

  get isMfaPhoneEntryValid() {
    // While the shared field is driving, its verdict is the only one: the digit count
    // would let a NANP shape it rejects through the +1 fallback.
    if (this.showCaptureStates) {
      return this.mfaPhoneEntryE164 !== null;
    }

    const digits = this.mfaPhoneEntryValue.replace(/\D/g, '');
    return digits.length === 10 || (digits.length === 11 && digits.startsWith('1'));
  }

  get normalizedMfaPhoneEntryValue() {
    return this.mfaPhoneEntryE164 ?? toUsE164(this.mfaPhoneEntryValue);
  }

  setMfaPhoneEntryValue = (value: string) => {
    this.mfaPhoneEntryValue = value || '';
    this.mfaPhoneEntryE164 = null;
  };

  setValidMfaPhoneEntryValue = (e164: string | null) => {
    this.mfaPhoneEntryE164 = e164;
  };

  onMfaPhoneEntrySubmit = () => {
    if (!this.isMfaPhoneEntryValid) return;
    this.showMfaPhoneEntry = false;
    this.verificationMode = 'mfa-phone-entry';
  };

  onMfaPhoneEntryCancel = () => {
    this.showMfaPhoneEntry = false;
    this.mfaPhoneEntryValue = '';
  };

  updateMfaSettings = dropTask(async (isSelected: boolean) => {
    if (!this.login) {
      return;
    }

    if (isSelected) {
      // If a verification code was already sent for enabling MFA, reopen verification directly
      if (this.hasActiveCode && this.codeSentForMode === 'mfa-enable') {
        this.verificationMode = 'mfa-enable';
        return;
      }

      if (!this.isPhoneNumberValid) {
        // Preserve previously entered phone if a code was sent for it
        if (!(this.hasActiveCode && this.codeSentForMode === 'mfa-phone-entry')) {
          this.mfaPhoneEntryValue = '';
        }
        this.showMfaPhoneEntry = true;
        return;
      }

      const phone = this.primaryPhoneNumber;
      if (phone?.isVerified && !this.isPhoneChangeInFlight) {
        this.login.twoFactorEnabled = true;
        await saveRecord(this.store, this.login as LoginModel);
      } else {
        this.verificationMode = 'mfa-enable';
      }
    } else {
      this.login.twoFactorEnabled = false;
      await saveRecord(this.store, this.login as LoginModel);
    }
  });

  get showVerification() {
    return !!this.verificationMode;
  }

  get verificationPhoneNumber() {
    if (this.verificationMode === 'mfa-phone-entry') {
      return this.normalizedMfaPhoneEntryValue;
    }
    return this.mfaPhoneNumber;
  }

  get verificationNewPhoneNumber() {
    if (this.verificationMode === 'phone-change') {
      return this.normalizedPhoneValue;
    }
    if (this.verificationMode === 'mfa-phone-entry') {
      return this.normalizedMfaPhoneEntryValue;
    }
    return undefined;
  }

  get verificationAutoSend() {
    if (this.verificationInitialStep === 'enter-code') return false;
    return this.verificationMode === 'phone-change' || this.verificationMode === 'mfa-phone-entry';
  }

  get hasActiveCode() {
    if (!this.codeSentAt || !this.codeSentForMode) return false;
    const TEN_MINUTES = 10 * 60 * 1000;
    return Date.now() - this.codeSentAt < TEN_MINUTES;
  }

  get verificationInitialStep() {
    if (!this.hasActiveCode || !this.verificationMode) return undefined;
    if (this.verificationMode !== this.codeSentForMode) return undefined;
    if (this.verificationPhoneNumber !== this.codeSentForPhone) return undefined;
    return 'enter-code' as const;
  }

  private clearCodeSentState() {
    this.codeSentAt = undefined;
    this.codeSentForMode = undefined;
    this.codeSentForPhone = undefined;
  }

  onCodeSent = () => {
    if (this.verificationMode !== 'phone-change') {
      this.codeSentAt = Date.now();
      this.codeSentForMode = this.verificationMode as 'mfa-enable' | 'mfa-phone-entry';
      this.codeSentForPhone = this.verificationPhoneNumber;
    }
  };

  onVerificationComplete = async () => {
    const mode = this.verificationMode;
    this.verificationMode = undefined;
    this.clearCodeSentState();

    if (mode === 'mfa-enable' || mode === 'mfa-phone-entry') {
      if (this.login) {
        this.login.twoFactorEnabled = true;
        await saveRecord(this.store, this.login as LoginModel);
      }
    }

    // Reload to get updated phone records (standalone refresh, NOT after a
    // builder mutation on this record) — keep the refetch via the helper.
    if (this.args.employee) {
      await reloadRecord(this.store, this.args.employee, { include: 'phone-numbers' });
    } else if (this.args.familyPerson) {
      await reloadRecord(this.store, this.args.familyPerson, { include: 'phone-numbers' });
    }

    if (mode === 'phone-change' || mode === 'mfa-phone-entry') {
      this.setInitialPhoneValue();
    }
    if (mode === 'phone-change') {
      this.isEditingContact = false;
    }
    if (mode === 'mfa-phone-entry') {
      this.mfaPhoneEntryValue = '';
    }
  };

  onVerificationCancel = () => {
    const mode = this.verificationMode;
    this.verificationMode = undefined;

    // For phone-change, always clear codeSent state (security-sensitive)
    if (mode === 'phone-change') {
      this.clearCodeSentState();
    }

    // Only clear phone entry value if no active code was sent for this flow
    if (mode === 'mfa-phone-entry') {
      const codeSentForThisFlow = this.hasActiveCode && this.codeSentForMode === 'mfa-phone-entry';
      if (!codeSentForThisFlow) {
        this.mfaPhoneEntryValue = '';
      }
    }
  };

  <template>
    <Section>
      <:header>{{t "profile.security_settings"}}</:header>
      <:body>
        <div class="tio-col-flush">
          <div class="flex flex-row mx-8 my-4">
            <div class="self-center w-1/4 font-semibold tio-fg-secondary">
              {{t "profile.personal_info"}}
            </div>
            <div class="tio-col-flush w-1/5">
              <span class="text-xs">{{t "login.first_name"}}</span>
              <span class="ml-4 text-xl my-4">{{this.person.firstName}}</span>
            </div>
            <div class="tio-col-flush w-1/5">
              <span class="text-xs">{{t "login.last_name"}}</span>
              <span class="ml-4 text-xl my-4">{{this.person.lastName}}</span>
            </div>
            <div class="flex-col ml-auto w-1/6 tio-fg-muted grid justify-items-center">
              <PhLock @size="24" @weight="fill" />
              <span class="text-xs">{{t "profile.to_edit_contact_support"}}</span>
            </div>
          </div>
          <hr />
          <div class="flex flex-row mx-8 my-4">
            <div class="self-center w-1/4 font-semibold tio-fg-secondary">
              {{t "profile.username_login"}}
            </div>
            <div class="tio-col-flush w-1/5">
              <span class="text-xs">{{t "profile.username"}}</span>
              <span class="text-xl my-4 ml-4">{{this.userName}}</span>
            </div>
            <div class="flex-col ml-auto w-1/6 tio-fg-muted grid justify-items-center">
              <PhLock @size="24" @weight="fill" />
              <span class="text-xs">{{t "profile.to_edit_contact_support"}}</span>
            </div>
          </div>
          <hr />
          <div class="flex flex-row mx-8 my-4">
            <div class="self-center w-1/4 font-semibold tio-fg-secondary">
              {{t "profile.contact_info"}}
            </div>
            <div class="tio-col-flush">
              {{#if this.isEditingContact}}
                {{#if this.showCaptureStates}}
                  <PhoneInput
                    @testHook="profile-phone"
                    @value={{this.phoneValue}}
                    @onChange={{this.setPhoneValue}}
                    @onValidChange={{this.setValidPhoneValue}}
                    @label={{t "profile.mobile_phone"}}
                  />
                {{else}}
                  <Input
                    @value={{this.phoneValue}}
                    @type="tel"
                    @onChange={{this.setPhoneValue}}
                    @onBlur={{this.onPhoneBlur}}
                    @label={{t "profile.mobile_phone"}}
                    placeholder="+1 (###) ###-####"
                    {{inputmask mask="+1 (999) 999-9999"}}
                    data-test-phone-input
                  />
                {{/if}}
              {{else if this.showCaptureStates}}
                {{#if this.captureNumber}}
                  <div class="tio-row my-4 ml-4">
                    <span class="tio-text-xl-normal">{{formatPhone this.captureNumber}}</span>
                    {{#if this.isCaptureVerified}}
                      <StatusBadge
                        @intent="success"
                        @icon={{PhSealCheck}}
                        @label={{t "profile.phone_capture.verified"}}
                        data-test-profile-phone-verified-badge
                      />
                    {{else if this.isCaptureUnverified}}
                      <StatusBadge
                        @intent="warning"
                        @icon={{PhClock}}
                        @label={{t "profile.phone_capture.unverified"}}
                        data-test-profile-phone-unverified-badge
                      />
                    {{/if}}
                  </div>

                  {{#if this.isCaptureVerified}}
                    <span
                      class="tio-text-xs-normal tio-fg-muted ml-4"
                      data-test-profile-phone-verified-on
                    >
                      {{t
                        "profile.phone_capture.verified_on"
                        date=(safeFormatDate
                          this.captureVerifiedAt month="short" day="numeric" year="numeric"
                        )
                      }}
                    </span>
                  {{else if this.isCaptureUnverified}}
                    <Button
                      @intent="primary"
                      @size="sm"
                      class="self-start ml-4"
                      {{on "click" this.openPhoneVerification}}
                      data-test-profile-phone-verify-now
                    >
                      <PhPaperPlaneTilt @size={{16}} />
                      {{t "profile.phone_capture.verify_now"}}
                    </Button>
                    <span
                      class="tio-text-xs-normal tio-fg-muted ml-4"
                      data-test-profile-phone-verify-hint
                    >
                      {{t "profile.phone_capture.verify_hint"}}
                    </span>
                  {{/if}}
                {{else}}
                  {{! A member with only a landline still has a number on file; the
                      capture branch used to hide it while inviting them to add one. }}
                  {{#if this.phoneValue}}
                    <span class="tio-text-xl-normal my-4 ml-4" data-test-profile-phone-non-mobile>
                      {{formatPhone this.phoneValue}}
                    </span>
                  {{/if}}
                  <Button
                    @appearance="outlined"
                    @intent="primary"
                    class="self-start my-4 ml-4"
                    {{on "click" this.openPhoneVerification}}
                    data-test-profile-phone-add
                  >
                    {{t "profile.phone_capture.add_number"}}
                  </Button>
                {{/if}}
              {{else if this.phoneValue}}
                <span class="text-xl my-4 ml-4">
                  {{formatPhone this.phoneValue}}
                </span>
              {{else}}
                <span class="text-xs my-4 ml-4 tio-fg-danger">
                  {{t "profile.no_phone"}}
                </span>
              {{/if}}
            </div>
            <div class="tio-flex-end flex-row self-center ml-auto w-1/6 gap-x-2">
              {{#if this.isEditingContact}}
                <Button
                  @appearance="outlined"
                  @intent="primary"
                  {{on "click" this.savePhoneNumber.perform}}
                  @isDisabled={{this.isSubmitDisabled}}
                  data-test-contact-save
                >
                  {{t "common.save"}}
                </Button>
                <Button
                  @appearance="outlined"
                  @intent="danger"
                  {{on "click" this.toggleContactEdit}}
                  data-test-contact-cancel
                >
                  {{t "common.cancel"}}
                </Button>
              {{else}}
                <Button
                  @appearance="outlined"
                  @intent="primary"
                  {{on "click" this.toggleContactEdit}}
                  class="self-auto"
                  data-test-contact-edit
                >
                  {{t "common.edit"}}
                </Button>
              {{/if}}
            </div>
          </div>
          {{#if this.showCaptureStates}}
            <hr />
            <div class="flex flex-row mx-8 my-4" data-test-profile-text-updates>
              <div class="self-center w-1/4 tio-text-base-semibold tio-fg-secondary">
                {{t "profile.phone_capture.text_updates"}}
              </div>
              <div class="tio-col-flush w-2/5">
                <span class="tio-text-xs-normal">{{t
                    "profile.phone_capture.text_updates_description"
                  }}</span>
              </div>
              <div class="tio-flex-end flex-row self-center ml-auto w-1/6 gap-x-2">
                <Switch
                  @checked={{this.isSmsConsentOn}}
                  @isDisabled={{true}}
                  @label={{t "profile.phone_capture.text_updates_label"}}
                  @hint={{t this.smsConsentHint}}
                />
              </div>
            </div>
          {{/if}}
          <hr />
          <div class="flex flex-row mx-8 my-4" data-test-mfa-section>
            <div class="self-center w-1/4 font-semibold tio-fg-secondary">
              {{t "profile.mfa.title"}}
            </div>
            <div class="tio-col-flush w-2/5">
              <span class="text-xs">{{t "profile.mfa.status"}}</span>
              <span class="text-xl my-4 ml-4" data-test-mfa-status>
                {{if
                  this.isMfaEnabled
                  (t "profile.mfa.status_enabled")
                  (t "profile.mfa.status_disabled")
                }}
              </span>
              {{#if this.showMfaPhonePrompt}}
                <span class="text-xs tio-fg-danger ml-4" data-test-mfa-phone-required>
                  {{t "profile.mfa.phone_required"}}
                </span>
              {{else if this.showMfaPhoneNumber}}
                <span class="text-xs tio-fg-muted ml-4" data-test-mfa-phone-number>
                  {{t "profile.mfa.sms_number"}}
                  {{formatPhone this.mfaPhoneNumber}}
                </span>
              {{/if}}
            </div>
            <div class="tio-flex-end flex-row self-center ml-auto w-1/6 gap-x-2">
              <div data-test-mfa-toggle>
                <Switch
                  @checked={{this.isMfaEnabled}}
                  @onChange={{this.updateMfaSettings.perform}}
                  @isDisabled={{this.isMfaToggleDisabled}}
                  @label={{t "profile.mfa.toggle_label"}}
                />
              </div>
            </div>
          </div>
        </div>
      </:body>
    </Section>

    {{#if this.showCaptureStates}}
      <Modal
        @isOpen={{this.showPhoneVerification}}
        @onClose={{this.closePhoneVerification}}
        @size="md"
        data-test-profile-phone-verification
      >
        <:body>
          <PhoneVerificationFlow
            @surface="profile"
            @phoneNumber={{this.captureNumber}}
            @onVerified={{this.onPhoneVerified}}
            @onCancel={{this.closePhoneVerification}}
          />
        </:body>
      </Modal>
    {{/if}}

    {{! MFA / Phone Verification Modal (consolidated) }}
    <MfaVerification
      @show={{this.showVerification}}
      @phoneNumber={{this.verificationPhoneNumber}}
      @newPhoneNumber={{this.verificationNewPhoneNumber}}
      @autoSend={{this.verificationAutoSend}}
      @initialStep={{this.verificationInitialStep}}
      @onVerified={{this.onVerificationComplete}}
      @onCancel={{this.onVerificationCancel}}
      @onCodeSent={{this.onCodeSent}}
    />

    {{! Phone Change Warning }}
    <Modal
      @isOpen={{this.showPhoneChangeWarning}}
      @onClose={{this.cancelPhoneChange}}
      @size="sm"
      data-test-phone-change-warning
    >
      <:header>
        <Heading @level={{3}}>
          {{t "profile.mfa.phone_change_warning.title"}}
        </Heading>
      </:header>
      <:body>
        <p class="text-sm tio-fg-secondary">
          {{t "profile.mfa.phone_change_warning.message"}}
        </p>
      </:body>
      <:footer>
        <div class="tio-flex-end gap-3">
          <Button
            @appearance="outlined"
            @intent="default"
            {{on "click" this.cancelPhoneChange}}
            data-test-phone-change-cancel
          >
            {{t "profile.mfa.phone_change_warning.cancel"}}
          </Button>
          <Button
            @intent="primary"
            {{on "click" this.confirmPhoneChange}}
            data-test-phone-change-continue
          >
            {{t "profile.mfa.phone_change_warning.continue"}}
          </Button>
        </div>
      </:footer>
    </Modal>

    {{! MFA Phone Entry Modal (no phone on file) }}
    <Modal
      @isOpen={{this.showMfaPhoneEntry}}
      @onClose={{this.onMfaPhoneEntryCancel}}
      @size="md"
      data-test-mfa-phone-entry
    >
      <:header>
        <div class="tio-row">
          <div class="w-10 h-10 rounded-xl tio-bg-brand-soft tio-row-center">
            <PhDeviceMobile class="tio-fg-brand" @size={{20}} />
          </div>
          <Heading @level={{2}} @size="lg">
            {{t "profile.mfa.phone_entry.title"}}
          </Heading>
        </div>
      </:header>
      <:body>
        <div class="space-y-4">
          <p class="text-sm tio-fg-secondary">
            {{t "profile.mfa.phone_entry.description"}}
          </p>
          {{#if this.showCaptureStates}}
            <PhoneInput
              @testHook="mfa-phone-entry"
              @value={{this.mfaPhoneEntryValue}}
              @onChange={{this.setMfaPhoneEntryValue}}
              @onValidChange={{this.setValidMfaPhoneEntryValue}}
              @label={{t "profile.mobile_phone"}}
            />
          {{else}}
            <Input
              @value={{this.mfaPhoneEntryValue}}
              @type="tel"
              @onChange={{this.setMfaPhoneEntryValue}}
              @label={{t "profile.mobile_phone"}}
              placeholder="+1 (###) ###-####"
              {{inputmask mask="+1 (999) 999-9999"}}
              data-test-mfa-phone-entry-input
            />
          {{/if}}
        </div>
      </:body>
      <:footer>
        <div class="tio-flex-end gap-3">
          <Button
            @appearance="outlined"
            @intent="default"
            {{on "click" this.onMfaPhoneEntryCancel}}
            data-test-mfa-phone-entry-cancel
          >
            {{t "common.cancel"}}
          </Button>
          <Button
            @intent="primary"
            @isDisabled={{not this.isMfaPhoneEntryValid}}
            {{on "click" this.onMfaPhoneEntrySubmit}}
            data-test-mfa-phone-entry-continue
          >
            {{t "profile.mfa.phone_entry.continue"}}
          </Button>
        </div>
      </:footer>
    </Modal>
  </template>
}
